import { useT } from "@transifex/react";
import { useEffect, useRef } from "react";
import PhaseSixIcon from "../phaseSixIcon/PhaseSixIcon";
import { AreaButton, ButtonVariant } from "../../complex/teacherArea/AreaButton";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";

interface IframeModalProps {
    url: string;
    title: string;
    isOpen: boolean;
    onClose: () => void;
}

const IframeModal = (props: IframeModalProps) => {
    const dialogRef = useRef<HTMLDialogElement>(null);

    const { getStyleBasedOnBreakpoint } = useBreakpoints();
    const onClose = props.onClose;
    const t = useT();
    const t_close = t("Close", { _tags: "TeacherArea,IframeModal" });

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        if (props.isOpen) {
            dialog.showModal();
            document.body.style.overflow = "hidden";
        } else if (dialog.open) {
            dialog.close();
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [props.isOpen]);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        const handleCancel = (event: Event) => {
            event.preventDefault();
            onClose();
        };

        dialog.addEventListener("cancel", handleCancel);
        return () => dialog.removeEventListener("cancel", handleCancel);
    }, [onClose]);

    const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
        if (event.target === dialogRef.current) {
            props.onClose();
        }
    };

    return (
        <dialog
            ref={dialogRef}
            className={`m-auto max-h-[90vh] max-w-none overflow-hidden rounded-medium border border-p6-grey-lightest bg-white p-no shadow-xl backdrop:bg-transparent-white ${getStyleBasedOnBreakpoint(
                Breakpoint.MD,
                "w-[882px] h-[735px]",
                "w-[calc(100vw-var(--spacing-l))] h-[550px]"
            )}`}
            onClick={handleBackdropClick}
        >
            <div className="flex h-full flex-col overflow-clip rounded-medium">
                <div className="flex items-end justify-end px-s pt-s pb-xs">
                    <button
                        className="flex cursor-pointer items-center justify-center border-none bg-transparent p-no text-xl text-p6-grey-dark"
                        onClick={props.onClose}
                        aria-label={t_close}
                    >
                        <PhaseSixIcon name="close" />
                    </button>
                </div>

                <div className="relative min-h-0 flex-1 overflow-hidden">
                    <iframe
                        src={props.url}
                        title={props.title}
                        sandbox="allow-same-origin allow-scripts allow-modals allow-popups allow-forms"
                        className={`border-none origin-top-left ${getStyleBasedOnBreakpoint(
                            Breakpoint.MD,
                            "h-full w-full",
                            "h-[125%] w-[125%] scale-[0.8]"
                        )}`}
                    />
                </div>

                <div className="flex items-center justify-center px-l py-s">
                    <AreaButton
                        buttonId="iframe-modal-close-btn"
                        variant={ButtonVariant.SECONDARY}
                        role="button"
                        onClick={props.onClose}
                    >
                        {t_close}
                    </AreaButton>
                </div>
            </div>
        </dialog>
    );
};

export default IframeModal;
